<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { toNumber, type Money } from '@/modules/shared/utils/money'
import { rails_url } from '@/modules/shared/utils/rails'
import { VCard } from '@/modules/shared/components'
import type { DescriptionListDetailedItem } from '@/modules/shared/components/types'
import Portfolio360Overview from '@/modules/investing/components/overview/portfolio-360-overview.vue'

import TaskList from '@/modules/tasking/components/TaskList.vue'
import ServicesTaskList from '@/modules/tasking/components/ServicesTaskList.vue'
import TaskListPaymentBanner from '@/modules/tasking/components/TaskListPaymentBanner.vue'
import EinUploadModal from '@/modules/tasking/components/EinUploadModal.vue'
import { type Task } from '@/modules/tasking/types'
import OverviewShell from '@/modules/investing/components/overview/overview-shell.vue'
import OverviewRaising from '@/modules/investing/components/overview/raising.vue'
import OverviewClosedExited from '@/modules/investing/components/overview/closed-exited.vue'
import RaiseSummary from '@/modules/investing/components/overview/raise-summary.vue'
import InvestmentSummaryCard from '@/modules/investing/components/overview/investment-summary-card.vue'

import { useEntityOverview } from '@/modules/investing/composables/use-entity-overview'

const {
  route: { entity_id, slug, entityType },
  stores: { filingStore, taskListStore },
  state: { entity, isAdmin, isSiteAdmin, skeleton, init },
  taskList: { taskListId, isBillingSettled },
  lifecycle: { lifecycleState, isRaising, isClosed, isExited },
  deal: {
    dealCard,
    structure,
    isMemberView,
    hasStructure,
    showMetrics,
    investorCount,
    investmentCompletedCount,
    companyHref,
  },
  chart: { metricSeriesPoints, metricSeriesCurrency, metricSeriesLoading },
  filing: { dealFiles, closingDocuments, exitDocuments },
  chartInsights,
  raising: { totalCommitted, targetRaise },
  metrics: {
    fundsDeployed,
    totalDistributed,
    cashPosition,
    roi,
    moic,
    insightsXirr,
    realizedGain,
    unrealizedGain,
    docsSignedCount,
    docsTotalCount,
    paidCalled,
    totalCalled,
  },
  summary: { summaryLabel, summaryDate, summaryStatus },
  actions: {
    handleCloseSubmit,
    handleReopen,
    handleReclose,
    fetchExitReadiness,
    fetchCloseReadiness,
    handleExitSubmit,
  },
} = useEntityOverview()

///////////////////////////////////////////////////////////////////////////////
// Entity-type-derived values
///////////////////////////////////////////////////////////////////////////////

const router = useRouter()

const isFund = computed(() => entityType === 'fund')
const isSPV = computed(() => entityType === 'spv')

const completeInvestmentHref = computed(() => {
  if (isFund.value) return `${rails_url()}/companies`
  return dealCard.value?.company_id ? `${rails_url()}/companies/${dealCard.value.company_id}/terms/new` : null
})

const handleRaiseAction = (item: DescriptionListDetailedItem) => {
  if (item.label === 'Documents Signed') {
    router.push({ name: 'investing.entity.documents', params: { entity_id, entity_type: entityType } })
  } else if (item.label === 'Capital Paid') {
    router.push({ name: 'investing.capital-call.new', params: { entity_id, entity_type: entityType } })
  } else if (item.label === 'Investment Completed' && completeInvestmentHref.value) {
    window.location.href = completeInvestmentHref.value
  }
}

///////////////////////////////////////////////////////////////////////////////
// Unified deal-card shape
///////////////////////////////////////////////////////////////////////////////

const dealInfo = computed(() => {
  const dc = dealCard.value
  return {
    name: dc?.name ?? entity.value?.name ?? '—',
    description: dc?.description ?? '',
    sectors: dc?.sectors ?? null,
    stage: dc?.investment_stage ?? null,
    equityType: isFund.value ? (dc?.fund_type ?? null) : (dc?.investment_type ?? null),
    preMoneyValuation: (dc?.pre_money_valuation as Money | null) ?? null,
    minimumInvestment: (dc?.minimum_commitment as Money | null) ?? null,
    expectedCloseDate: dc?.expected_close_date ?? '',
    url: isFund.value ? '' : (dc?.website ?? ''),
    fundSize: (dc?.target_raise as Money | null) ?? null,
  }
})

const dealCardProps = computed(() => ({
  entityType,
  collapsible: false,
  name: dealInfo.value.name,
  url: dealInfo.value.url,
  companyHref: companyHref.value,
  description: dealInfo.value.description,
  sectors: dealInfo.value.sectors,
  stage: dealInfo.value.stage,
  equityType: dealInfo.value.equityType,
  preMoneyValuation: dealInfo.value.preMoneyValuation,
  targetRaise: targetRaise.value,
  minimumInvestment: dealInfo.value.minimumInvestment,
}))

///////////////////////////////////////////////////////////////////////////////
// SPV-only: services task upload — file is prefixed with the task name and
// dropped in the SPV's filing root. Task auto-completes on a successful upload.
///////////////////////////////////////////////////////////////////////////////

const uploadInput = ref<HTMLInputElement | null>(null)
const uploadingTask = ref<Task | null>(null)
const uploadingTaskId = ref<number | null>(null)
const einModalRef = ref<InstanceType<typeof EinUploadModal> | null>(null)
const einTask = ref<Task | null>(null)

const handleUploadRequest = (task: Task) => {
  // The EIN task gets a dedicated modal (EIN field + optional file). We match
  // on automation_trigger rather than name so the wiring survives copy edits
  // to the YAML template.
  if (task.automation_trigger === 'has_tax_id') {
    einTask.value = task
    einModalRef.value?.modal.open()
    return
  }
  uploadingTask.value = task
  uploadInput.value?.click()
}

const handleFileChosen = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  const task = uploadingTask.value
  const folderId = entity.value?.compliance_filing_folder_id ?? entity.value?.filing_folder_root_id

  if (!file || !task || !folderId) {
    uploadingTask.value = null
    input.value = ''
    return
  }

  uploadingTaskId.value = task.id
  try {
    const prefixed = new File([file], `${task.name} - ${file.name}`, { type: file.type })
    await filingStore.addFile(prefixed, folderId, Number(entity_id), 'InvestorSet' as any)
    await taskListStore.completeTask(task.id)
  } catch (err) {
    console.error('Services task upload failed:', err)
  } finally {
    uploadingTask.value = null
    uploadingTaskId.value = null
    input.value = ''
  }
}

///////////////////////////////////////////////////////////////////////////////
// Lifecycle
///////////////////////////////////////////////////////////////////////////////

onMounted(init)
</script>

<template>
  <OverviewShell
    :entityType="entityType"
    :isRaising="isRaising"
    :isClosed="isClosed"
    :isExited="isExited"
    :isAdmin="isAdmin"
    :isSiteAdmin="isSiteAdmin"
    :lifecycleState="lifecycleState"
    :skeleton="skeleton"
    :onCloseSubmit="handleCloseSubmit"
    :onExitSubmit="handleExitSubmit"
    :fetchExitReadiness="fetchExitReadiness"
    :fetchCloseReadiness="fetchCloseReadiness"
    :completeInvestmentHref="completeInvestmentHref"
    :onReopen="handleReopen"
    :onReclose="handleReclose"
  >
    <input v-if="isSPV" type="file" ref="uploadInput" class="hidden" @change="handleFileChosen" />
    <EinUploadModal
      v-if="isSPV"
      ref="einModalRef"
      :task="einTask"
      :spvId="entity_id"
      :slug="slug"
      :filingFolderRootId="entity?.compliance_filing_folder_id ?? entity?.filing_folder_root_id"
      :currentEin="entity?.tax_id"
    />

    <!-- Top-of-shell payment banner — SPV billing surface on the v2 services task list -->
    <TaskListPaymentBanner
      v-if="isAdmin && taskListId && isSPV"
      class="mb-8"
      :entityType="entityType"
      :billingSettled="isBillingSettled"
      :billingRoute="{ name: 'billing-entity', params: { billable_id: entity_id, billable_type: entityType } }"
    />

    <!-- Raising state -->
    <OverviewRaising
      v-if="isRaising"
      :skeleton="false"
      :showKpi="isFund"
      :roi="roi"
      :moic="moic"
      :xirr="insightsXirr"
      :realizedGain="realizedGain"
      :unrealizedGain="unrealizedGain"
    >
      <template v-if="isMemberView" #raise-summary>
        <RaiseSummary
          :entityType="entityType"
          :isAdmin="isAdmin"
          :totalCommitted="totalCommitted"
          :targetRaise="targetRaise"
          :investorCount="investorCount"
          :paidCalled="paidCalled"
          :totalCalled="totalCalled"
          :docsSigned="docsSignedCount"
          :docsTotal="docsTotalCount"
          :investmentCompletedCount="investmentCompletedCount"
          @add-commitment="
            router.push({
              name: 'investing.investors',
              params: { slug, entity_type: entityType, entity_id },
              query: { add_commitment: 'true' },
            })
          "
          @action-click="handleRaiseAction"
        />
      </template>

      <template #details>
        <InvestmentSummaryCard
          :entityType="entityType"
          :name="dealInfo.name"
          :url="dealInfo.url"
          :description="dealInfo.description"
          :sectors="dealInfo.sectors"
          :stage="dealInfo.stage"
          :equityType="dealInfo.equityType"
          :preMoneyValuation="dealInfo.preMoneyValuation"
          :targetRaise="targetRaise"
          :minimumInvestment="dealInfo.minimumInvestment"
          :expectedCloseDate="dealInfo.expectedCloseDate"
          :hasStructure="hasStructure"
          :managementFee="structure.managementFee"
          :carriedInterest="structure.carriedInterest"
          :preferredReturn="structure.preferredReturn"
          :files="dealFiles"
        />
      </template>
      <template v-if="isAdmin && taskListId" #task-list>
        <VCard>
          <ServicesTaskList
            v-if="isSPV"
            :slug="slug"
            :taskListId="taskListId"
            :uploadingTaskId="uploadingTaskId"
            @upload="handleUploadRequest"
          />
          <TaskList v-else :slug="slug" :taskListId="taskListId" />
        </VCard>
      </template>
    </OverviewRaising>

    <!-- Closed / Exited state -->
    <OverviewClosedExited
      v-if="isClosed || isExited"
      :skeleton="false"
      :showMetrics="showMetrics"
      :showKpi="isFund || showMetrics"
      :investorCount="investorCount"
      :totalCommitted="totalCommitted"
      :paidCalled="paidCalled"
      :isClosed="isClosed"
      :isExited="isExited"
      :exitDate="summaryDate"
      :exitType="(entity as any)?.exit_type ?? null"
      :grossProceeds="totalDistributed"
      :initialValue="fundsDeployed"
      :cashPosition="cashPosition"
      :roi="roi"
      :moic="moic"
      :xirr="insightsXirr"
      :realizedGain="realizedGain"
      :unrealizedGain="unrealizedGain"
      :calledSettled="chartInsights.calledSettledTotal.value"
      :calledPending="chartInsights.calledPendingTotal.value"
      :calledTotal="chartInsights.calledTotal.value"
      :committedAmount="toNumber(totalCommitted)"
      :managementFees="chartInsights.calledManagementFees.value"
      :otherFees="chartInsights.calledOtherFees.value"
      :fundsDeployed="toNumber(fundsDeployed)"
      :cashPositionAmount="toNumber(cashPosition)"
      :operatingIncome="chartInsights.calledOperatingIncome.value"
      :operatingExpenses="chartInsights.calledOperatingExpense.value"
      :interestPaid="chartInsights.interestAllocationPaid.value"
      :interestReceived="chartInsights.interestAllocationReceived.value"
      :distributedPaid="chartInsights.distributedGross.value"
      :distributedPending="chartInsights.unpaidDistributedGross.value"
      :returnOfCapital="chartInsights.distributedReturnOfCapital.value"
      :preferredReturn="chartInsights.distributedPreferredReturn.value"
      :profit="chartInsights.distributedProfit.value"
      :carriedInterestAmount="chartInsights.distributedCarriedInterest.value"
      :netDistributed="chartInsights.distributedNet.value"
      :distributedOtherFees="chartInsights.distributedOtherFee.value"
      :recycledFunds="chartInsights.calledRecycledFunds.value"
      :hurdle="chartInsights.totalHurdle.value"
      :currency="entity?.currency ?? 'USD'"
    >
      <template #summary>
        <Portfolio360Overview
          :isAdmin="isAdmin"
          :insights="entity?.insights"
          :companies="entity?.companies ?? []"
          :industries="entity?.industries ?? []"
          :metricSeriesPoints="metricSeriesPoints"
          :metricSeriesCurrency="metricSeriesCurrency"
          :metricSeriesLoading="metricSeriesLoading"
        />
        <InvestmentSummaryCard
          :entityType="entityType"
          :name="dealInfo.name"
          :url="dealInfo.url"
          :description="dealInfo.description"
          :sectors="dealInfo.sectors"
          :stage="dealInfo.stage"
          :equityType="dealInfo.equityType"
          :preMoneyValuation="dealInfo.preMoneyValuation"
          :targetRaise="targetRaise"
          :minimumInvestment="dealInfo.minimumInvestment"
          :expectedCloseDate="summaryDate"
          :lifecycleState="summaryStatus"
          :hasStructure="hasStructure"
          :managementFee="structure.managementFee"
          :carriedInterest="structure.carriedInterest"
          :preferredReturn="structure.preferredReturn"
          :files="isClosed ? [...dealFiles, ...closingDocuments] : [...dealFiles, ...exitDocuments]"
        />
      </template>
      <template v-if="isAdmin && taskListId" #task-list>
        <VCard>
          <ServicesTaskList
            v-if="isSPV"
            :slug="slug"
            :taskListId="taskListId"
            :uploadingTaskId="uploadingTaskId"
            @upload="handleUploadRequest"
          />
          <TaskList v-else :slug="slug" :taskListId="taskListId" />
        </VCard>
      </template>
    </OverviewClosedExited>
  </OverviewShell>
</template>
